"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useGetPizzaByType } from "@/api/pizza-checker/pizza-types/pizza-types";
import { usePostGetAllPizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, ChefHat, Package, Clock, Eye } from "lucide-react";
import { PizzaType, Component, PizzasDTO, PizzaDTO } from "@/api/custom-schema";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { formatDateTime } from "@/lib/date-format";

/** Display names of the annotation keys (the keys themselves are data). */
const ANNOTATION_LABEL_KEYS: Record<string, TranslationKey> = {
  Raw: "pizzaTypes.annotation.raw",
  Burnt: "pizzaTypes.annotation.burnt",
  Components: "pizzaTypes.annotation.ingredients",
};

interface PizzaTypeDetailProps {
  pizzaTypeId: string;
}

export default function PizzaTypeDetail({ pizzaTypeId }: PizzaTypeDetailProps) {
  const { t, tp } = useLocalization();
  const { data: pizzaTypeResponse, isLoading, error } = useGetPizzaByType(pizzaTypeId);
  const branchFilter = useBranchFilter();
  const navigate = useNavigate();
  
  // Type the API response properly
  const pizzaType = pizzaTypeResponse as PizzaType | undefined;

  // Fetch recent pizzas for this pizza type
  const { data: recentPizzasResponse, isLoading: pizzasLoading } = usePostGetAllPizzas<PizzasDTO>(
    {
      limit: 6,
      skip: 0,
      sort_by: "capture_at",
      sort_order: -1,
      filters: {
        ...branchFilter,
        // Filter by pizza type name if available
        ...(pizzaType?.name && { name: { $regex: pizzaType.name, $options: "i" } }),
      },
    },
    {
      query: {
        enabled: !!pizzaType?.name, // Only fetch when we have the pizza type name
      },
    },
  );

  const recentPizzas = recentPizzasResponse?.pizzas || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            <Skeleton className="h-6 w-32" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-18" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            {t("pizzaTypes.detail.loadFailedTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 p-4 border border-destructive/50 rounded-md bg-destructive/10">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <p className="text-sm text-destructive">
              {t("pizzaTypes.detail.loadFailed")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!pizzaType) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            {t("pizzaTypes.detail.notFoundTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 p-4 border rounded-md bg-muted/50">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm text-muted-foreground">
              {t("pizzaTypes.detail.notFound")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ChefHat className="h-5 w-5" />
          {pizzaType.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">{t("pizzaTypes.detail.id")}</div>
            <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
              {pizzaType.id}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">{t("pizzaTypes.columns.ingredientCount")}</div>
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              <Badge variant="secondary">
                {tp("pizzaTypes.detail.ingredientCount", pizzaType.components?.length || 0)}
              </Badge>
            </div>
          </div>
        </div>

        {pizzaType.components && pizzaType.components.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />
              {t("pizzaTypes.columns.ingredients")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pizzaType.components.map((component: Component, index: number) => (
                <Card key={index} className="p-3">
                  <div className="space-y-2">
                    <div className="font-medium text-sm">{component.name}</div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {(!pizzaType.components || pizzaType.components.length === 0) && (
          <div className="flex items-center gap-2 p-4 border rounded-md bg-muted/50">
            <Package className="h-4 w-4" />
            <p className="text-sm text-muted-foreground">
              {t("pizzaTypes.detail.noIngredients")}
            </p>
          </div>
        )}

        {/* Recent Pizzas Section */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t("pizzaTypes.detail.recentPizzas", { count: recentPizzas.length })}
          </div>
          
          {pizzasLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-3">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-6 w-16" />
                  </div>
                </Card>
              ))}
            </div>
          ) : recentPizzas.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentPizzas.map((pizza: PizzaDTO) => (
                <Card key={pizza.id} className="p-3 hover:shadow-md transition-shadow cursor-pointer" 
                      onClick={() => navigate(`/pizzas/${pizza.id}`)}>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm truncate">{pizza.name}</div>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Eye className="h-3 w-3" />
                      </Button>
                    </div>
                    
                    {pizza.capture_at && (
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(pizza.capture_at)}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1 flex-wrap">
                      {pizza.evaluation && (
                        <Badge variant="outline" className="text-xs">
                          {t("pizzaTypes.detail.evaluated")}
                        </Badge>
                      )}
                      {pizza.order_id && (
                        <Badge variant="secondary" className="text-xs">
                          {t("pizzaTypes.detail.order", { id: pizza.order_id.slice(-6) })}
                        </Badge>
                      )}
                    </div>

                    {pizza.annotations && (
                      <div className="flex items-center gap-1">
                        {Object.entries(pizza.annotations).map(([key, value]) => (
                          value && value !== "None" && (
                            <Badge 
                              key={key} 
                              variant={value === "OK" ? "default" : "destructive"} 
                              className="text-xs"
                            >
                              {ANNOTATION_LABEL_KEYS[key] ? t(ANNOTATION_LABEL_KEYS[key]) : key}:{" "}
                              {value === "OK"
                                ? t("status.ok")
                                : value === "NOK"
                                  ? t("status.nok")
                                  : value}
                            </Badge>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-4 border rounded-md bg-muted/50">
              <Clock className="h-4 w-4" />
              <p className="text-sm text-muted-foreground">
                {t("pizzaTypes.detail.noRecentPizzas")}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}